import { useEffect, useMemo, useRef } from 'react'
import type { Control, FieldErrors, UseFormSetValue } from 'react-hook-form'
import { Controller, useWatch } from 'react-hook-form'
import { NumericTextField } from 'ui/components/NumericTextField'
import { CreateFormGridItem } from 'ui/ManagerFinance/components'
import styled from 'styled-components'
import { formatCurrency } from 'util/formatStrings'
import { ErrorMessage } from 'ui/ManagerFinance/utils/components'
import type { LoanEventCreateFormInputs } from './validation'
import { FormattedMessage, useIntl } from 'react-intl'
import type { LoanCalculation } from '@viilu-web/shared'

const NotificationMessage = styled.small`
	font-size: 0.7em;
`

type Props = {
	calculation: LoanCalculation | null
	shareGroupUUID: string | undefined | null
	control: Control<LoanEventCreateFormInputs>
	errors: FieldErrors<LoanEventCreateFormInputs>
	setValue: UseFormSetValue<LoanEventCreateFormInputs>
	disabled: boolean
}

function LoanEventCreateAdjustedAmountInput({
	calculation,
	shareGroupUUID,
	control,
	errors,
	disabled,
	setValue,
}: Props) {
	const intl = useIntl()
	const balanceAdjustmentAmountRef = useRef<number | undefined>(0)

	const [
		eventDate,
		amount,
		adjustedAmount,
		balanceAdjustmentAmount,
		scaledBalanceAdjustmentAmount,
		units,
		bankExpenseAmount,
		otherExpenseAmount,
	] = useWatch({
		name: [
			'eventDate',
			'amount',
			'adjustedAmount',
			'balanceAdjustmentAmount',
			'scaledBalanceAdjustmentAmount',
			'units',
			'bankExpenseAmount',
			'otherExpenseAmount',
		],
		control,
	})

	const shareholderCalculationForSelectedShareGroup = useMemo(() => {
		return calculation?.shareholderCalculations.find(
			(sg) => sg.shareGroupUUID === shareGroupUUID,
		)
	}, [calculation, shareGroupUUID])

	useEffect(() => {
		const currentSharegroupBalance = calculation?.shareholderCalculations.find(
			(sg) => sg.shareGroupUUID === shareGroupUUID,
		)?.balance
		if (balanceAdjustmentAmountRef.current !== currentSharegroupBalance) {
			setValue('balanceAdjustmentAmount', currentSharegroupBalance)
			balanceAdjustmentAmountRef.current = currentSharegroupBalance
		}
	}, [calculation, setValue, shareGroupUUID])

	useEffect(() => {
		const selectedUnitsPercentage =
			(Number(units) || 0) /
			(shareholderCalculationForSelectedShareGroup?.units || 1)
		const newScaledBalanceAdjustmentAmount =
			balanceAdjustmentAmount && calculation
				? balanceAdjustmentAmount * selectedUnitsPercentage
				: 0
		setValue('scaledBalanceAdjustmentAmount', newScaledBalanceAdjustmentAmount)
	}, [
		units,
		balanceAdjustmentAmount,
		calculation,
		setValue,
		shareholderCalculationForSelectedShareGroup,
	])

	useEffect(() => {
		if (amount >= 0) {
			const selectedUnitsPercentage =
				(Number(units) || 0) /
				(shareholderCalculationForSelectedShareGroup?.units || 1)
			const newAdjustedAmount =
				amount +
				(Number(balanceAdjustmentAmount) * selectedUnitsPercentage || 0)
			setValue('adjustedAmount', newAdjustedAmount)
		}
	}, [
		amount,
		balanceAdjustmentAmount,
		setValue,
		units,
		shareholderCalculationForSelectedShareGroup,
	])

	useEffect(() => {
		const newAmount =
			amount + (bankExpenseAmount || 0) + (otherExpenseAmount || 0)
		setValue('amountWithExpenses', newAmount)
	}, [amount, bankExpenseAmount, otherExpenseAmount, setValue])

	const amountKey = 'amountWithExpenses'
	const adjustedAmountKey = 'adjustedAmount'
	const unitsKey = 'units'

	if (!eventDate || !shareGroupUUID) {
		return (
			<NotificationMessage>
				<FormattedMessage
					defaultMessage="Valitse ensin tapahtuman päivämäärä ja osakeryhmä, jotta voit määrittää suorituksen määrän."
					description="Notification message when event date or share group is not selected"
				/>
			</NotificationMessage>
		)
	}

	return (
		<>
			<CreateFormGridItem
				key={amountKey}
				$fullWidth={true}
				style={{
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<NotificationMessage>
					<FormattedMessage
						defaultMessage={`Huoneiston tämänhetkinen lainaosuus: {amount}`}
						description="Sharegroup/apartment current loan share"
						values={{
							amount:
								calculation !== undefined
									? formatCurrency(
											shareholderCalculationForSelectedShareGroup?.baseShareAmount ||
												0,
									  )
									: '-',
						}}
					/>
				</NotificationMessage>
				<NotificationMessage>
					<FormattedMessage
						defaultMessage={`Huoneiston tämänhetkiset velalliset yksiköt: {amount}`}
						description="Sharegroup/apartment current debtor units"
						values={{
							amount:
								calculation !== undefined
									? shareholderCalculationForSelectedShareGroup?.units || 0
									: '-',
						}}
					/>
				</NotificationMessage>
				<NotificationMessage>
					<FormattedMessage
						defaultMessage={`Yksittäisen velallisen yksikön hinta: {amount}`}
						description="Sharegroup/apartment calculated balance"
						values={{
							amount:
								calculation?.loanShareAmount !== undefined
									? formatCurrency(calculation.loanShareAmount)
									: '-',
						}}
					/>
				</NotificationMessage>
				<NotificationMessage>
					<FormattedMessage
						defaultMessage={`Huoneiston laskettu kokonaisjäämä: {amount}`}
						description="Sharegroup/apartment calculated balance"
						values={{
							amount:
								balanceAdjustmentAmount !== undefined
									? formatCurrency(balanceAdjustmentAmount)
									: '-',
						}}
					/>
				</NotificationMessage>

				<NotificationMessage>
					<FormattedMessage
						defaultMessage={`Huoneiston suhteutettu jäämä {units} velallisella yksiköllä: {amount}`}
						description="Sharegroup/apartment calculated balance"
						values={{
							amount:
								scaledBalanceAdjustmentAmount !== undefined
									? formatCurrency(scaledBalanceAdjustmentAmount)
									: '-',
							units: units !== undefined ? units : '-',
						}}
					/>
				</NotificationMessage>
				<Controller
					name={amountKey}
					control={control}
					render={({ field: { onChange, value: currentValue } }) => (
						<NumericTextField
							id="amount-numeric-input"
							label={intl.formatMessage({
								defaultMessage: 'Suorituksen määrä (€)',
								description: 'Repayment amount',
							})}
							required={true}
							lineDirection="center"
							floating
							fullWidth
							error={errors[amountKey] !== undefined}
							disabled={disabled}
							value={currentValue}
							onChange={(value) => {
								onChange(value)

								const numValue = Number(value) || 0
								const totalExpenses =
									(bankExpenseAmount || 0) + (otherExpenseAmount || 0)
								const amountWithoutExpenses = numValue - totalExpenses
								if (amountWithoutExpenses < 0) {
									setValue('bankExpenseAmount', 0)
									setValue('otherExpenseAmount', 0)
									setValue('amountWithExpenses', 0)
									return
								}
								setValue('amount', numValue - totalExpenses)
								if (calculation) {
									const selectedUnitsPercentage =
										(Number(units) || 0) /
										(shareholderCalculationForSelectedShareGroup?.units || 1)
									const adjustedAmount =
										amountWithoutExpenses +
										(Number(balanceAdjustmentAmount) *
											selectedUnitsPercentage || 0)
									const amountInUnits =
										adjustedAmount / calculation.loanShareAmount
									if (amountInUnits !== units) {
										setValue('units', amountInUnits)
									}
								}
							}}
							min={0}
						/>
					)}
				/>
				{errors[amountKey]?.message && (
					<ErrorMessage style={{ marginBottom: '0.5rem' }}>
						{errors[amountKey]?.message}
					</ErrorMessage>
				)}
			</CreateFormGridItem>
			<CreateFormGridItem key={adjustedAmount} $fullWidth={false}>
				<Controller
					name={adjustedAmountKey}
					control={control}
					render={({ field: { onChange, value: currentValue } }) => (
						<NumericTextField
							id="adjusted-amount-numeric-input"
							label={intl.formatMessage({
								defaultMessage: 'Oikaistu määrä',
								description: 'Adjusted amount',
							})}
							required={true}
							lineDirection="center"
							floating
							fullWidth
							error={errors[adjustedAmountKey] !== undefined}
							disabled={true}
							value={currentValue as number}
							onChange={onChange}
							min={0}
						/>
					)}
				/>
			</CreateFormGridItem>
			<CreateFormGridItem key={unitsKey} $fullWidth={false}>
				<Controller
					name={unitsKey}
					control={control}
					render={({ field: { onChange, value: currentValue } }) => (
						<NumericTextField
							id="units-numeric-input"
							label={intl.formatMessage({
								defaultMessage: 'Yksiköt',
								description: 'Units',
							})}
							required={true}
							lineDirection="center"
							floating
							fullWidth
							error={errors[unitsKey] !== undefined}
							disabled={disabled}
							value={currentValue as number}
							onChange={(value) => {
								onChange(value)
								const numValue = Number(value) || 0
								const newAdjustedAmount =
									calculation?.loanShareAmount && balanceAdjustmentAmount
										? numValue * calculation.loanShareAmount
										: 0
								const selectedUnitsPercentage =
									(numValue || 0) /
									(shareholderCalculationForSelectedShareGroup?.units || 1)
								const newBaseAmount =
									newAdjustedAmount > 0
										? newAdjustedAmount -
										  (Number(balanceAdjustmentAmount) *
												selectedUnitsPercentage || 0)
										: 0
								const totalExpenses =
									(bankExpenseAmount || 0) + (otherExpenseAmount || 0)
								const newAmountWithExpenses = newBaseAmount + totalExpenses
								if (newAdjustedAmount !== adjustedAmount) {
									setValue('amountWithExpenses', newAmountWithExpenses)
									setValue('amount', newBaseAmount)
								}
							}}
							min={0}
						/>
					)}
				/>
				{errors[unitsKey]?.message && (
					<ErrorMessage style={{ marginBottom: '0.5rem' }}>
						{errors[unitsKey]?.message}
					</ErrorMessage>
				)}
			</CreateFormGridItem>
		</>
	)
}

export default LoanEventCreateAdjustedAmountInput
